import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useInspections } from '@/hooks/useInspections';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useAuth } from '@/hooks/useAuth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ArrowLeft, Edit, Trash2, Calendar, FileText, Image as ImageIcon, Save, X, Shield } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { SignedPhotoImage } from '@/components/crm/SignedPhotoImage';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { useToast } from '@/hooks/use-toast';
import { UnifiedFilePreview } from '@/components/shared/UnifiedFilePreview';
import { normalizeStoragePath } from '@/lib/storageUtils';

const INSPECTION_TYPES = [
  'Veiligheid',
  'Onderhoud',
  'Installatie',
  'Keuring',
  'Controle',
  'Anders'
];

export default function InspectorInspectionDetail() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user: portalUser } = usePortalAuth();
  const { user: crmUser, hasRole } = useAuth();
  const { deleteInspection, updateInspection } = useInspections();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editType, setEditType] = useState('');
  const [editCustomType, setEditCustomType] = useState('');
  const [editDate, setEditDate] = useState<Date | undefined>(undefined);
  const [editNotes, setEditNotes] = useState('');

  const { data: inspection, isLoading, error: inspectionError } = useQuery({
    queryKey: ['inspection-detail', inspectionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inspections')
        .select('*')
        .eq('id', inspectionId!)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!inspectionId,
  });

  const { data: projectData } = useQuery({
    queryKey: ['inspection-project', inspection?.project_id],
    queryFn: async () => {
      if (!inspection?.project_id) return null;
      
      const { data, error } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          project_number,
          org_id,
          organization:organizations (
            id,
            name
          )
        `)
        .eq('id', inspection.project_id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!inspection?.project_id,
  });

  const handleDelete = () => {
    if (inspectionId) {
      deleteInspection(inspectionId);
      navigate('/inspector/history');
    }
  };

  const handleEdit = () => {
    if (!inspection) return;
    setEditType(inspection.type);
    setEditCustomType(INSPECTION_TYPES.includes(inspection.type) ? '' : inspection.type);
    setEditDate(new Date(inspection.date));
    setEditNotes(inspection.notes || '');
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!inspection || !inspectionId) return;
    
    const finalType = editType === 'Anders' ? editCustomType : editType;
    
    if (!finalType || !editDate) {
      toast({
        title: 'Ontbrekende velden',
        description: 'Vul alle verplichte velden in',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateInspection({
        id: inspectionId,
        type: finalType,
        date: format(editDate, 'yyyy-MM-dd'),
        notes: editNotes || null,
      });
      
      setIsEditing(false);
      toast({
        title: 'Opgeslagen',
        description: 'Inspectie is bijgewerkt',
      });
    } catch (error: any) {
      toast({
        title: 'Fout',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      concept: 'bg-gray-100 text-gray-800',
      ingediend: 'bg-blue-100 text-blue-800',
      verwerkt: 'bg-green-100 text-green-800',
    };
    return variants[status] || variants.concept;
  };

  // Hybrid authentication: support both portal users and CRM users
  const currentUserId = portalUser?.id || crmUser?.id;
  const isPortalInspector = !!portalUser && portalUser.role === 'inspector';
  const isCRMAdmin = hasRole('admin') || hasRole('super_admin') || hasRole('crm_user');
  
  // Permissions
  const isOwnInspection = inspection?.inspector_id === currentUserId;
  const canEdit = isCRMAdmin || (isPortalInspector && isOwnInspection && inspection?.status === 'concept');
  const canDelete = isCRMAdmin || (isPortalInspector && isOwnInspection && inspection?.status === 'concept');

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (inspectionError) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-12 text-center">
          <h3 className="text-lg font-semibold mb-2">Fout bij laden</h3>
          <p className="text-muted-foreground mb-4">{(inspectionError as any).message}</p>
          <Button onClick={() => navigate('/inspector/history')}>Terug naar geschiedenis</Button>
        </Card>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-12 text-center">
          <h3 className="text-lg font-semibold mb-2">Inspectie niet gevonden</h3>
          <p className="text-muted-foreground mb-4">Deze inspectie bestaat niet of je hebt geen toegang.</p>
          <Button onClick={() => navigate('/inspector/history')}>Terug naar geschiedenis</Button>
        </Card>
      </div>
    );
  }

  const photos = inspection.photo_urls && Array.isArray(inspection.photo_urls) ? inspection.photo_urls : [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold">{inspection.type}</h1>
              <p className="text-muted-foreground mt-1">
                {projectData?.name}
                {projectData?.organization && Array.isArray(projectData.organization) && projectData.organization[0]?.name && ` • ${projectData.organization[0].name}`}
              </p>
            </div>
            {canEdit && !isEditing && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Bewerken
                </Button>
                {canDelete && (
                  <Button variant="destructive" size="sm" onClick={() => setShowDeleteDialog(true)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Verwijderen
                  </Button>
                )}
              </div>
            )}
            {isEditing && (
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveEdit}>
                  <Save className="h-4 w-4 mr-2" />
                  Opslaan
                </Button>
                <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                  <X className="h-4 w-4 mr-2" />
                  Annuleren
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Access Type Indicator */}
      {isCRMAdmin && (
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            {isOwnInspection 
              ? 'Je bewerkt deze inspectie als beheerder (eigen inspectie)'
              : 'Je bewerkt deze inspectie als beheerder'}
          </AlertDescription>
        </Alert>
      )}
      {isPortalInspector && isOwnInspection && (
        <Alert className="border-blue-200 bg-blue-50">
          <Shield className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Inspector toegang - Je eigen inspectie
          </AlertDescription>
        </Alert>
      )}

      {/* Details Card */}
      <Card className="p-6 space-y-4">
        {!isEditing ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Status</p>
                <Badge className={getStatusBadge(inspection.status)}>
                  {inspection.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Datum</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>{format(new Date(inspection.date), 'PPP', { locale: nl })}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Type</p>
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>{inspection.type}</span>
                </div>
              </div>
            </div>

            {inspection.notes && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Notities</p>
                <p className="text-sm whitespace-pre-wrap bg-muted p-3 rounded-md">
                  {inspection.notes}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Type Inspectie</label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSPECTION_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editType === 'Anders' && (
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Geef type aan</label>
                <Input
                  value={editCustomType}
                  onChange={(e) => setEditCustomType(e.target.value)}
                  placeholder="Bijv. Noodstroomtest"
                />
              </div>
            )}

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Inspectiedatum</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !editDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editDate ? format(editDate, 'PPP', { locale: nl }) : 'Selecteer datum'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={editDate}
                    onSelect={setEditDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Notities</label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Eventuele bijzonderheden of bevindingen..."
                rows={4}
              />
            </div>
          </div>
        )}
      </Card>

      {/* Photos */}
      {photos.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="h-5 w-5" />
            <h2 className="text-xl font-semibold">Foto's ({photos.length})</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo: string, index: number) => (
              <div
                key={index}
                className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedPhotoIndex(index)}
              >
                <SignedPhotoImage
                  photoRef={photo}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inspectie verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze inspectie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnifiedFilePreview
        file={selectedPhotoIndex !== null && photos[selectedPhotoIndex] ? {
          name: `Foto ${selectedPhotoIndex + 1}`,
          bucket: 'portal-uploads',
          path: normalizeStoragePath(String(photos[selectedPhotoIndex])),
        } : null}
        files={photos.map((url, idx) => ({
          name: `Foto ${idx + 1}`,
          bucket: 'portal-uploads',
          path: normalizeStoragePath(String(url)),
        }))}
        open={selectedPhotoIndex !== null}
        onOpenChange={(open) => !open && setSelectedPhotoIndex(null)}
        onNavigate={(direction) => {
          if (selectedPhotoIndex === null) return;
          const newIndex = direction === 'next'
            ? Math.min(photos.length - 1, selectedPhotoIndex + 1)
            : Math.max(0, selectedPhotoIndex - 1);
          setSelectedPhotoIndex(newIndex);
        }}
        portal="inspector"
      />
    </div>
  );
}
